// ═══════════════════════════════════════════════════════════════════════════
//  MARKET HOURS — exchange-segment-aware IST clock (single source).
//
//  NSE/BSE equity: Mon-Fri 09:15-15:30 IST (pre-open 09:00-09:15)
//  MCX commodity:  Mon-Fri 09:00-23:30 IST (Apr-Oct) / 09:00-23:55 (Nov-Mar,
//                  US daylight-saving linkage) — clock-based, holidays ignored.
//
//  The screener previously ran ONE NSE-only clock, so at 19:00 IST the
//  COMMODITY tab / MCX PCR showed "MARKET CLOSED" while MCX was live.
// ═══════════════════════════════════════════════════════════════════════════

export type MarketSegment = 'NSE' | 'BSE' | 'MCX' | 'MCX_AGRI';
export type SegmentStatus = 'OPEN' | 'PRE_OPEN' | 'CLOSED';

/** IST wall-clock independent of the browser timezone. */
export function istClock(now: Date = new Date()): { mins: number; day: number; month: number } {
  const ist = new Date(now.getTime() + 5.5 * 3600 * 1000);
  return {
    mins: ist.getUTCHours() * 60 + ist.getUTCMinutes(),
    day: ist.getUTCDay(),
    month: ist.getUTCMonth() + 1,
  };
}

/** MCX evening close: 23:55 in US-winter months (Nov-Mar), else 23:30. */
export function mcxCloseMin(month: number): number {
  return (month >= 11 || month <= 3) ? 23 * 60 + 55 : 23 * 60 + 30;
}

const NSE_OPEN = 9 * 60 + 15;
// NSE RULE CHANGE (effective 2026-08-03, God-verified): F&O trades till 15:40
// (was 15:30); cash CAS 15:15-15:35. UI segment-status F&O close par chalta
// hai (Vega/Shool ka maidan) — cash CAS nuance backend exchange_clock me hai.
const NSE_CLOSE = 15 * 60 + 40;
const MCX_OPEN = 9 * 60;
const MCX_AGRI_CLOSE = 21 * 60;

export function segmentWindow(seg: MarketSegment, now: Date = new Date()): { open: number; close: number } {
  const { month } = istClock(now);
  if (seg === 'MCX') return { open: MCX_OPEN, close: mcxCloseMin(month) };
  if (seg === 'MCX_AGRI') return { open: MCX_OPEN, close: MCX_AGRI_CLOSE };
  return { open: NSE_OPEN, close: NSE_CLOSE };
}

export function segmentStatus(seg: MarketSegment, now: Date = new Date()): SegmentStatus {
  const { mins, day } = istClock(now);
  if (day === 0 || day === 6) return 'CLOSED';
  const { open, close } = segmentWindow(seg, now);
  if (seg !== 'MCX' && seg !== 'MCX_AGRI' && mins >= 9 * 60 && mins < open) return 'PRE_OPEN';
  return mins >= open && mins < close ? 'OPEN' : 'CLOSED';
}

export const isSegmentOpen = (seg: MarketSegment, now: Date = new Date()): boolean =>
  segmentStatus(seg, now) === 'OPEN';

/** Human label for the MCX close used in UI copy ("11:30 PM" / "11:55 PM"). */
export function mcxCloseLabel(now: Date = new Date()): string {
  return mcxCloseMin(istClock(now).month) === 23 * 60 + 55 ? '11:55 PM' : '11:30 PM';
}
